import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { watchToggleCopy, type WatchType as WatchTypeName } from '@/lib/watchLabels';
import { trackActivity } from '@/lib/activityTracking';
import { announceWatchLimit, isWatchLimitError } from '@/lib/watchLimit';

export type WatchCadence = 'instant' | 'daily' | 'weekly' | 'off';
export type WatchType = WatchTypeName;

export interface WatchTarget {
  watch_id: string;
  watch_type: WatchType;
  listing_id: string | null;
  supplier_company_id: string | null;
  saved_search_id: string | null;
  menu_id: string | null;
  cadence: WatchCadence;
}

export const WATCH_TARGETS_KEY = ['watch-targets'];

/**
 * All watch targets for the caller's company. One small query powers every
 * watch button on the page, so buttons never fan out into per-row requests.
 */
export function useWatchTargets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: WATCH_TARGETS_KEY,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async (): Promise<WatchTarget[]> => {
      const { data, error } = await supabase.rpc('get_my_watch_targets');
      if (error) throw error;
      return Array.isArray(data) ? (data as unknown as WatchTarget[]) : [];
    },
  });
}

interface ToggleArgs {
  watchType: WatchType;
  listingId?: string | null;
  supplierCompanyId?: string | null;
  savedSearchId?: string | null;
  menuId?: string | null;
  cadence?: WatchCadence;
  /** Current state — decides whether we add or remove. */
  isWatched: boolean;
  label?: string;
}

export function useToggleWatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: ToggleArgs) => {
      if (args.isWatched) {
        const { error } = await supabase.rpc('remove_watch_by_target', {
          p_watch_type: args.watchType,
          p_listing_id: args.listingId ?? null,
          p_supplier_company_id: args.supplierCompanyId ?? null,
          p_saved_search_id: args.savedSearchId ?? null,
          p_menu_id: args.menuId ?? null,
        });
        if (error) throw error;
        return { added: false };
      }
      const { error } = await supabase.rpc('add_watch', {
        p_watch_type: args.watchType,
        p_listing_id: args.listingId ?? null,
        p_supplier_company_id: args.supplierCompanyId ?? null,
        p_saved_search_id: args.savedSearchId ?? null,
        p_cadence: args.cadence ?? null,
        p_menu_id: args.menuId ?? null,
      });
      if (error) throw error;
      return { added: true };
    },
    onSuccess: (result, args) => {
      queryClient.invalidateQueries({ queryKey: WATCH_TARGETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      /* Investment rung: following something is the buyer's stored intent. */
      trackActivity(result.added ? 'watch_added' : 'watch_removed', {
        watch_type: args.watchType,
        listing_id: args.listingId ?? null,
        supplier_company_id: args.supplierCompanyId ?? null,
        menu_id: args.menuId ?? null,
        saved_search_id: args.savedSearchId ?? null,
      });
      const copy = watchToggleCopy(args.watchType, result.added);
      if (result.added) {
        toast.success(copy.title, { description: copy.description });
      } else {
        toast.info(copy.title);
      }
    },
    onError: (error: Error, args) => {
      if (isWatchLimitError(error)) {
        announceWatchLimit(args.watchType === 'search' ? 'search' : 'watch');
        return;
      }
      toast.error('Could not update your watchlist', { description: 'Please try again in a moment.' });
    },
  });
}

export function useSetWatchCadence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ watchId, cadence }: { watchId: string; cadence: WatchCadence }) => {
      const { error } = await supabase.rpc('set_watch_cadence', {
        p_watch_id: watchId,
        p_cadence: cadence,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCH_TARGETS_KEY });
      queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      toast.success('Alert frequency updated');
    },
    onError: (error: Error) => toast.error('Could not change frequency', { description: error.message }),
  });
}